import { Injectable, isDevMode } from '@angular/core';
import { createStore } from '@ngneat/elf';
import {
  deleteAllEntities,
  deleteEntities,
  getActiveEntity,
  getActiveId,
  getAllEntities,
  getEntity,
  selectActiveEntity,
  selectAllEntities,
  setActiveId,
  updateEntities as updateEntity,
  upsertEntities,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState, localStorageStrategy } from '@ngneat/elf-persist-state';
import { Subject, map } from 'rxjs';
import { ELF_PREFIX } from 'src/app/app.constants';
import { TimestampEntity } from 'src/app/timer/state/timer.interface';
import { dayKeyFor } from 'src/app/utilities/day-key.utils';

export interface HistoryEntity {
  id: string;
  timestamps: TimestampEntity[];
  goal: number | null | undefined; // target goal in minutes of productive time
  multiplier: number; // multiplier for the goal
  baseRevision?: number; // remote revision this day was last adopted-from/written-as; undefined = 0
  dirty?: boolean; // unsynced local changes; undefined = false
  modifiedAt?: number; // epoch ms of last LOCAL user mutation (fork tie-breaker)
}

const name = ELF_PREFIX + 'history';

const store = createStore({ name }, withEntities<HistoryEntity>(), withActiveId());

export const persist = persistState(store, {
  key: name,
  storage: localStorageStrategy,
});

@Injectable({
  providedIn: 'root',
})
export class HistoryRepository {
  historyDates$ = store.pipe(selectAllEntities());

  currentTimestamps$ = store.pipe(
    selectActiveEntity(),
    map((entity) => entity?.timestamps || []),
  );

  currentGoal$ = store.pipe(
    selectActiveEntity(),
    map((entity) => entity?.goal),
  );

  currentMultiplier$ = store.pipe(
    selectActiveEntity(),
    map((entity) => entity?.multiplier),
  );

  reset$ = new Subject<boolean>();

  // Local user mutation: settings changes (goal/multiplier) always mark the day dirty.
  addSettings(settings: { goal?: number | null; multiplier?: number }, dateKey: string) {
    const entry = store.query(getEntity(dateKey));
    const dirtyState = { dirty: true, modifiedAt: Date.now() };

    if (entry) {
      store.update(
        upsertEntities({
          id: dateKey,
          ...settings,
          timestamps: entry.timestamps || [],
          ...dirtyState,
        }),
      );
    } else {
      store.update(upsertEntities([{ id: dateKey, ...settings, timestamps: [], ...dirtyState }]));
    }
  }

  // Local user mutation: entity list changes always mark the day dirty.
  //
  // dayStartHour is optional: passing it turns on the dev-mode day-log validity assertion (see
  // assertEntriesMatchDay below); omitting it just skips that check. Optional rather than required
  // so this repository's specs (which construct it with `new HistoryRepository()`, no Angular DI)
  // never need to thread a hour value through call sites that don't care.
  updateEntities(dateKey: string, entities: TimestampEntity[], dayStartHour?: number) {
    this.assertEntriesMatchDay(dateKey, entities, dayStartHour);
    store.update(
      upsertEntities({ id: dateKey, timestamps: entities, dirty: true, modifiedAt: Date.now() }),
    );
  }

  /**
   * Appends a single entry to `dayKey`. The only write path for new timer entries.
   *
   * Deliberately takes one entry rather than an array: a caller cannot read one day's timestamps
   * and write them to a different key, which is what duplicated a whole day across the boundary.
   *
   * dayStartHour is optional for the same reason as updateEntities' — see its doc comment.
   */
  appendEntry(dayKey: string, entry: TimestampEntity, dayStartHour?: number) {
    const existing = store.query(getEntity(dayKey));
    const timestamps = [...(existing?.timestamps ?? []), entry];
    this.assertEntriesMatchDay(dayKey, timestamps, dayStartHour);
    store.update(
      upsertEntities({
        id: dayKey,
        timestamps,
        dirty: true,
        modifiedAt: Date.now(),
      }),
    );
  }

  /**
   * Dev-mode-only safety net for the design spec's data-safety rule 1: every entry inside a day
   * entity must map back to that day's own key ("`dayKeyFor(entry.timestamp) === targetKey`").
   * This is defense-in-depth behind TimestampService's own guards (validateHistoryEdit's day-key
   * check, updateEntryTime's boundary clamp) — it exists to catch any OTHER write path, present or
   * future, that manages to violate the invariant.
   *
   * Never throws: a violation here means something upstream already computed the wrong day, and
   * crashing the app would be strictly worse than a bad timeline the next repair pass can fix.
   * `isDevMode()` is false in every production build (main.ts calls `enableProdMode()` whenever
   * `environment.production` is true), so this branch never runs for real users — it only logs
   * loudly, so a violation is impossible to miss in dev or CI.
   */
  private assertEntriesMatchDay(
    dayKey: string,
    timestamps: TimestampEntity[],
    dayStartHour?: number,
  ): void {
    if (!isDevMode() || dayStartHour === undefined) {
      return;
    }
    for (const entry of timestamps) {
      const actualKey = dayKeyFor(entry.timestamp, dayStartHour);
      if (actualKey !== dayKey) {
        console.error(
          `[day-log-validity] entry ${entry.id} (${entry.timestamp}) belongs to day ` +
            `${actualKey}, but is being written into ${dayKey}. This violates the day-log ` +
            'validity invariant (see docs/superpowers/specs/2026-08-20-logical-day-boundary-design.md).',
          entry,
        );
      }
    }
  }

  // Re-seeds a full day entity. No production caller remains: clearSyncedDays() (which replaced
  // clearAll()) never resets the store or re-seeds anything, since it only deletes days already
  // confirmed in the cloud. Kept only because history.repository.spec.ts's "legacy entities without
  // sync fields" tests use it to simulate data persisted before baseRevision/dirty/modifiedAt
  // existed — a dead-code candidate for the next review pass. Intentionally leaves
  // dirty/baseRevision/modifiedAt untouched: upsertEntities merges, so any existing sync state is
  // preserved as-is, and a brand-new entity is inserted exactly as given (no dirty flag introduced).
  addFullEntry(entry: HistoryEntity) {
    store.update(upsertEntities([entry]));
  }

  getEntries() {
    return store.query(getAllEntities());
  }

  getDay(dateKey: string): HistoryEntity | undefined {
    return store.query(getEntity(dateKey));
  }

  /**
   * Upserts a day adopted from the remote store: clean (dirty: false), stamped with the given
   * baseRevision, and leaving modifiedAt untouched (never invented here).
   */
  applyRemoteDay(day: {
    date: string;
    timestamps: TimestampEntity[];
    goal: number | null;
    multiplier: number;
    baseRevision: number;
  }) {
    store.update(
      upsertEntities({
        id: day.date,
        timestamps: day.timestamps,
        goal: day.goal,
        multiplier: day.multiplier,
        baseRevision: day.baseRevision,
        dirty: false,
      }),
    );
  }

  /**
   * Marks a day as successfully uploaded: always stamps the new baseRevision, but clears dirty ONLY
   * when the entity's current modifiedAt still equals the value the caller serialized
   * (expectedModifiedAt). A local mutation that landed while the write was in flight bumps
   * modifiedAt, so the mismatch preserves dirty and the follow-up save re-uploads the newer items —
   * this is what stops an in-flight save from clobbering a concurrent edit into permanent cloud
   * truncation. Two undefined modifiedAt values compare equal. No-op if the day doesn't exist.
   */
  markSynced(dateKey: string, baseRevision: number, expectedModifiedAt?: number) {
    const entity = store.query(getEntity(dateKey));
    if (!entity) {
      return;
    }
    const changes: Partial<HistoryEntity> = { baseRevision };
    if (entity.modifiedAt === expectedModifiedAt) {
      changes.dirty = false;
    }
    store.update(updateEntity(dateKey, changes));
  }

  /**
   * Sign-out cleanup. Deletes only days provably confirmed in the cloud (dirty === false exactly);
   * anything not yet flushed, or whose sync status is unknown (dirty === undefined — the shape of
   * entities persisted before the sync-fields schema existed, see the "legacy entities" spec
   * describe block), survives. Unknown status is treated as keep-not-delete, never as
   * synced-safe-to-delete: we cannot prove a legacy entity is already backed up in the cloud, and
   * guessing wrong here is a real data-loss bug (see history.repository.spec.ts's legacy-entity
   * coverage for clearSyncedDays). The previous behaviour kept the active entity as an arbitrary
   * survivor, which destroyed real history and preserved phantom days.
   */
  clearSyncedDays() {
    const synced = store
      .query(getAllEntities())
      .filter((entity) => entity.dirty === false)
      .map((entity) => entity.id);

    store.update(deleteEntities(synced));
    // The active pointer is a cache; the next reconcile() tick corrects it. Nothing to do here.
    this.reset$.next(true);
  }

  reset() {
    store.update(deleteAllEntities());
    store.reset();
    this.reset$.next(true);
  }

  /**
   * Removes specific days by id. Used when a day is proven to belong to a different account (see
   * FirebaseSyncService.start's account-switch ownership quarantine, the C2 fix): callers must
   * stash the content via the existing fork-backup mechanism BEFORE calling this, since it is a
   * hard local delete with no undo of its own.
   */
  removeDays(dayIds: string[]) {
    if (dayIds.length === 0) {
      return;
    }
    store.update(deleteEntities(dayIds));
  }

  getTodayTimestamps() {
    return store.query(getActiveEntity())?.timestamps || [];
  }

  getToday() {
    return store.query(getActiveEntity()) || null;
  }

  /**
   * The most recent day strictly before the active one, by date key. When no active id is set,
   * the `!activeId` branch of the filter admits every entity, so this instead returns the
   * globally most recent day rather than "before" anything — a deliberate fallback, not an
   * oversight. Ordering matters: elf keeps entities in insertion order, and a Firestore backfill
   * inserts historical days arbitrarily.
   */
  getLastUnactiveEntry() {
    const activeId = store.query(getActiveId) as string | undefined;

    const earlier = store
      .query(getAllEntities())
      .filter((entity) => !activeId || entity.id < activeId)
      .sort((a, b) => a.id.localeCompare(b.id));

    return earlier[earlier.length - 1] ?? null;
  }

  setActiveDay(dayKey: string) {
    store.update(setActiveId(dayKey));
  }

  /**
   * Removes days with no entries and no user-set goal. Empty days must never be materialised —
   * `addSettings` deliberately keeps a goal-only day (`timestamps: []`, `goal` non-null), which this
   * intentionally does NOT touch; only a day that is empty on both axes is dropped.
   */
  dropEmptyDays() {
    const empty = store
      .query(getAllEntities())
      .filter((entity) => (entity.timestamps ?? []).length === 0 && entity.goal == null)
      .map((entity) => entity.id);

    store.update(deleteEntities(empty));
  }
}
